import { isTrackedClass } from '../../interfaces/tracked-class';
import { IMaterialService } from '../../services/material-service';
import { IgnoreChanges } from '../decorators/change-tracker';
import { Enumerable } from '../decorators/enumerable';
import { BaseKb3dModel } from '../decorators/kb3d-decorator';
import { instanceOf, instanceOfAny } from '../decorators/model-utils';
import { defaults } from '../default-values';
import { eMaterialLocation, eMaterialViewMode, eReflectionType } from '../enums';
import { IMaterialNode, IReference, ITextureLayer } from '../kb3d-models';
import { Kb3dObject } from '../kb3d-object';
import { KbColor } from '../primitives/kb-color';
import { Token } from '../token';
import { isAbsoluteUrl } from '../utilities';
import { AdvancedMaterialNode } from './advanced-material-node';
import { MaterialMapping } from './material-mapping';
import { TextureLayer } from './texture-layer';

const NEVER_INHERIT = new Set<string>([
    '$type',
    'overriddenProperties',
    '_parentMaterial',
    'parentMaterialId',
    'location',
    'children',
    '_selected',
    '_expanded',
    'id',
    'name',
    'visible',
    '_parent',
    '_parentScene',
    '_rootScene',
    '_level',
]);

@BaseKb3dModel({
    token: Token.MaterialNode,
})
export abstract class MaterialNode extends MaterialMapping implements IMaterialNode {
    constructor(public _materialService: IMaterialService) {
        super();

        this._initialized = false;
    }

    protected _materials: MaterialNode[];
    @Enumerable()
    public get materials(): MaterialNode[] {
        if (!this._materials) this._materials = [];
        return this._materials;
    }
    public set materials(val) {
        this._materials = val;
    }

    @IgnoreChanges
    _initialized: boolean;

    // Location is where this material is actually defined/saved. Here we also assign the icon this material uses in the tree.
    // Local is unique to this scene
    // Global is saved to this tenant's account and can be used in any of this tenant's scenes
    // Library is defined by kbmax and can be used on any scene by any tenant.
    @IgnoreChanges
    location: eMaterialLocation;

    // Id of material to inherit from. This material will be loaded and the user can copy properties from this material.
    @IgnoreChanges
    parentMaterialId?: string;
    _parentMaterial?: IMaterialNode;

    public _overriddenPropertiesSet = new Set<string | symbol | number>();

    /** Implementation detail for persisting to database, do not use this property in the client code */
    private _overriddenProperties: string[];
    @Enumerable()
    public get overriddenProperties(): string[] {
        if (!this._overriddenProperties) this._overriddenProperties = [];
        return this._overriddenProperties;
    }
    public set overriddenProperties(val) {
        this._overriddenProperties = val;
    }

    /**
     * Archived: When this is set to true, material will be archived and
     * will not automatically be displayed when materials get loaded.
     */
    archived: boolean;

    private _textures: TextureLayer[];
    @Enumerable()
    public get textures(): TextureLayer[] {
        if (!this._textures) this._textures = [];
        return this._textures;
    }
    public set textures(val) {
        this._textures = val;
    }

    alpha: number;
    emissiveColor?: KbColor;
    viewMode: eMaterialViewMode;
    zOffset?: number;
    doubleSided: boolean;
    hasTransparency: boolean;
    probePositionTargetId?: string;
    reflectionType?: eReflectionType;
    @IgnoreChanges
    legacyTextureStacking: boolean;

    init() {
        this.refreshParentMaterial();

        if (!this._initialized) {
            if (isTrackedClass(this)) {
                // Always track changes on initialized materials because they may be global materials
                this.trackChanges(true);
                this.isModified = true;
            }
            this._initialized = true;

            this.textures.forEach(texture => {
                texture.init(this);
            });
        }
    }

    getChildren() {
        return [...this.textures, ...this.materials];
    }

    getChildArrayForType(token: Token): Kb3dObject[] | undefined {
        if (token.isMaterial()) {
            return this.materials;
        } else if (token.isTexture()) {
            return this.textures;
        } else {
            throw 'unexpected token';
        }
    }

    overrideProperty(property: string | symbol | number) {
        if (this.parentMaterialId) {
            this._overriddenPropertiesSet.add(property);
        }
    }

    inheritProperty(property: string | symbol | number) {
        if (this.parentMaterialId) {
            this._overriddenPropertiesSet.delete(property);

            if (this._parentMaterial) {
                (this as any)[property] =
                    (this._parentMaterial as any)[property] == null
                        ? (defaults as any)[this.$type][property]
                        : (this._parentMaterial as any)[property];
            }
        }
    }

    getTextureByName(name: string) {
        return [...this.textures].find(f => f.name.isEqual(name));
    }

    protected inheritFromMaterial(parentMat: IMaterialNode) {
        this.parentMaterialId = parentMat.id;
        this._parentMaterial = parentMat;
        if (!this._manager) {
            return;
        }
        const manager = this._manager;

        NEVER_INHERIT.forEach(prop => {
            this._overriddenPropertiesSet.add(prop);
        });

        for (const _key in parentMat) {
            const key = _key as keyof IMaterialNode;
            if (!this._overriddenPropertiesSet.has(key)) {
                //if this is a serialization of a texture node, then we need to deserialize it
                if (Array.isArray(parentMat[key])) {
                    const currentTextures = (this as any)[key] as TextureLayer[];
                    (parentMat[key] as ITextureLayer[]).forEach((tex, indexOnParent) => {
                        const texDef = { ...tex, name: `Base ${tex.name || tex.channel}` };
                        if (!texDef.id) {
                            texDef.id = `${parentMat.id}_${tex.channel}`;
                        }

                        const currentIndex = currentTextures.findIndex(
                            currentTexture => currentTexture.id === texDef.id
                        );
                        if (currentIndex < 0) {
                            const textureNode = manager.deserialize(texDef) as TextureLayer;
                            textureNode._external = true;
                            // If this material is already initialized, make sure the new texture is also initialized.
                            if (this._initialized) {
                                textureNode.init(this);
                            }
                            currentTextures.splice(indexOnParent, 0, textureNode);
                        } else {
                            // currentTextures.splice(currentIndex, 1, textureNode);
                        }
                    });
                } else {
                    (this as any)[key] =
                        parentMat[key] instanceof Kb3dObject ? parentMat[key] : manager.deserialize(parentMat[key]);
                }
            }
        }
    }

    public refreshParentMaterial() {
        if (this.parentMaterialId) {
            this._materialService.getMaterialById(this.parentMaterialId).subscribe(parentMaterial => {
                if (parentMaterial && parentMaterial.data) {
                    this.inheritFromMaterial(parentMaterial.data as any);
                }
            });
        }
    }

    public getMediaReferences() {
        const refs = new Array<IReference>();
        for (const texture of this.textures) {
            if (instanceOf(texture, Token.FileTextureLayer) && texture.path && !isAbsoluteUrl(texture.path)) {
                refs.push({
                    type: 'Media',
                    id: 'media/' + texture.path,
                });
            }
        }
        return refs;
    }
}

export function isMaterialNode(node: Kb3dObject): node is MaterialNode {
    return instanceOfAny<AdvancedMaterialNode>(node, ...Token.MaterialTokens);
}
